// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { type PreviouslyRejected } from "@mg/schemas/src/christo/askPunttReview";
import { CheckCircle, XCircle } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { type CommentWithOptionalTags } from "./reviewCommentTags";

import { formatMemoryDate } from "../../components/memory-citation/memoryLabels";

type Decider = { userId: string; name?: string } | undefined;

/** Who made a past decision, as the card says it. */
export type JudgedBy =
  | { kind: "you" }
  | { kind: "name"; name: string }
  | { kind: "role"; role: string }
  | { kind: "unknown" };

/**
 * The enterprise roles a judgment records (`EnterpriseProfileType`), each with
 * a label in `assetViewer.reviewerRoles`.
 *
 * why a closed list: the role is a storage slug, and the first live receipt
 * printed "Rejected before by a meaningful-gigs reviewer". A role outside this
 * list is never shown; the card says only that it was decided before.
 */
const LABELLED_ROLES = new Set([
  "meaningful-gigs",
  "admin",
  "admin-with-ai",
  "ops",
  "expert",
  "marketing",
  "creative",
  "requester",
  "agency",
  "freelancer",
  "employee",
  "guest",
]);

/**
 * The viewer's own decision first, then the decider's name, then a role label.
 * christo reads `decidedBy` from the judgment ledger when it serves the review
 * (`reviewJudgmentDeciders.ts`), so a receipt stored before it existed names
 * its decider too.
 */
export function judgedBy(
  decidedBy: Decider,
  role: string | undefined,
  currentUserId: string | undefined,
): JudgedBy {
  if (decidedBy?.userId && currentUserId && decidedBy.userId === currentUserId)
    return { kind: "you" };
  const name = decidedBy?.name?.trim();
  if (name) return { kind: "name", name };
  const slug = role?.trim().toLowerCase();
  if (slug && LABELLED_ROLES.has(slug)) return { kind: "role", role: slug };
  return { kind: "unknown" };
}

function receiptLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  verdict: "rejected" | "confirmed",
  who: JudgedBy,
): string {
  const stem =
    verdict === "rejected"
      ? "assetViewer.previouslyRejected"
      : "assetViewer.previouslyConfirmed";
  switch (who.kind) {
    case "you":
      return t(stem);
    case "name":
      return t(`${stem}ByName`, { name: who.name });
    case "role":
      return t(`${stem}By`, {
        role: t(`assetViewer.reviewerRoles.${who.role}`),
      });
    default:
      return t(`${stem}Unknown`);
  }
}

/**
 * The words a finding rejected in an earlier review carries on its card
 * ("Rejected before by <Name>"), for its grayed pin on the artwork to say too.
 */
export function previouslyRejectedReceiptLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  receipt: { decidedBy?: Decider; role?: string },
  currentUserId: string | undefined,
): string {
  return receiptLabel(
    t,
    "rejected",
    judgedBy(receipt.decidedBy, receipt.role, currentUserId),
  );
}

type PastReceipt = { decidedBy?: Decider; role?: string };

/**
 * Who rejected the finding in a past review: the ledger's receipt, else the
 * earlier comment's own reject, or null when no past review rejected it.
 *
 * why the fallback: the earlier-review link that printed this decision is
 * gone, and a reject only the earlier comment records still sits in the
 * Rejected section; without it the card would say nothing of why.
 */
export function pastRejectionReceipt(
  comment: Pick<
    CommentWithOptionalTags,
    "previouslyRejected" | "earlierComment"
  > & { restoredAt?: string | null },
): PastReceipt | null {
  // why: a restore in this review takes the past rejection back (christo stamps `restoredAt`); its receipt must go too
  if (comment.restoredAt != null) return null;
  if (comment.previouslyRejected) return comment.previouslyRejected;
  const earlier = comment.earlierComment;
  if (earlier?.disposition !== "rejected") return null;
  return { decidedBy: earlier.decidedBy };
}

/** Who approved the finding in a past review, as `pastRejectionReceipt` reads a reject. */
function pastApprovalReceipt(
  comment: Pick<
    CommentWithOptionalTags,
    "previouslyConfirmed" | "earlierComment"
  >,
): PastReceipt | null {
  if (comment.previouslyConfirmed) return comment.previouslyConfirmed;
  return comment.earlierComment?.disposition === "accepted" ? {} : null;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

type KnowledgeReceipt = Pick<
  PreviouslyRejected,
  "remembered" | "decidedBy" | "decidedAt" | "sourceName" | "knowledgeKind"
>;

/**
 * The receipt of a finding Puntt filed under Rejected because the
 * organisation's knowledge allows it (warhol spec dave.savostyanov/0092), or
 * null. Nobody rejected it, here or before, so the card says what allows it
 * instead (spec surbhi.kapoor/0097). A restore here takes it back, like every
 * past rejection.
 */
export function knowledgeOverrule(comment: {
  previouslyRejected?: PreviouslyRejected | null;
  restoredAt?: string | null;
}): PreviouslyRejected | null {
  if (comment.restoredAt != null) return null;
  const receipt = comment.previouslyRejected;
  return receipt?.matchedBy === "knowledge" ? receipt : null;
}

/**
 * What a knowledge overrule rests on: "Expert decision · Ana · 20 Oct", or
 * "Knowledge Database · <document>". Never a role slug or an ISO date: a
 * decider with no name is "an Expert", a day that does not parse is left off.
 */
export function knowledgeSourceLine(
  t: Translate,
  receipt: KnowledgeReceipt,
  language: string,
): string {
  // why the kind: an Expert's memory may lack a name or a day, so their presence cannot say who decided it
  if (receipt.knowledgeKind === "memory") {
    const name =
      receipt.decidedBy?.name?.trim() ||
      t("assetViewer.knowledgeOverruleAnExpert");
    const date = formatMemoryDate(receipt.decidedAt, language, {
      year: false,
    });
    return date
      ? t("assetViewer.knowledgeOverruleExpert", { name, date })
      : t("assetViewer.knowledgeOverruleExpertUndated", { name });
  }
  const document = receipt.sourceName?.trim();
  return document
    ? t("assetViewer.knowledgeOverruleDocument", { document })
    : t("assetViewer.knowledgeOverruleDocumentUnnamed");
}

// why only double marks: ' and ’ are apostrophes far more often than quotes ("gut's").
const QUOTE_MARK = /["“”„«»]/;

/**
 * The sentence a knowledge overrule rests on, as the card quotes it: inside
 * the reader's quote marks, or as written when it already carries its own,
 * so a sentence that quotes a headline never reads “The headline “Kefir…” …”.
 */
export function knowledgeQuote(t: Translate, quote: string): string {
  return QUOTE_MARK.test(quote)
    ? quote
    : t("assetViewer.knowledgeOverruleQuote", { quote });
}

/** The same receipt in one phrase, for the grayed pin on the artwork to say what its card says. */
export function knowledgeOverrulePinReceipt(
  t: Translate,
  receipt: KnowledgeReceipt,
  language: string,
): string {
  return t("assetViewer.knowledgeOverrulePinReceipt", {
    source: knowledgeSourceLine(t, receipt, language),
  });
}

/**
 * The receipt of a finding Puntt filed under Rejected because the
 * organisation's knowledge allows it: who rejected it (Puntt), the sentence
 * that allows it, and where that sentence lives. It replaces both rejection
 * labels, which would name a reviewer who never rejected it.
 */
function KnowledgeOverruleSubheading({
  receipt,
  commentId,
}: {
  receipt: KnowledgeReceipt;
  commentId: string;
}) {
  const { t, i18n } = useTranslation("ui");
  const quote = receipt.remembered.trim();
  const testId = `ask-v2-review-comment-knowledge-overrule-${commentId}`;
  return (
    <div className="mt-1 text-puntt-neutral-gray-11" data-testid={testId}>
      <Text
        as="p"
        size="2"
        weight="medium"
        className="flex items-center gap-1.5"
        data-testid={`${testId}-headline`}
      >
        <XCircle size={16} weight="bold" aria-hidden="true" />
        {t("assetViewer.knowledgeOverruled")}
      </Text>
      {quote ? (
        <Text
          as="p"
          size="2"
          className="mt-0.5 italic"
          data-testid={`${testId}-quote`}
        >
          {knowledgeQuote(t, quote)}
        </Text>
      ) : null}
      <Text as="p" size="1" className="mt-0.5" data-testid={`${testId}-source`}>
        {knowledgeSourceLine(t, receipt, i18n.language)}
      </Text>
    </div>
  );
}

/**
 * The receipt a demoted finding carries, as a subheading under its title.
 *
 * why: a small gray chip among the tag badges read as metadata; the first
 * live check had to open the card to find it at all. Directly under the title,
 * with the reject icon, it is the second thing she reads about the finding --
 * on the collapsed card and the open one alike, which are mutually exclusive
 * per comment, so the testid stays unique on the page.
 */
export function PreviouslyRejectedSubheading({
  comment,
  commentId,
  currentUserId,
}: {
  comment: CommentWithOptionalTags;
  commentId: string;
  currentUserId?: string;
}) {
  const { t } = useTranslation("ui");
  const knowledge = knowledgeOverrule(comment);
  if (knowledge) {
    return (
      <KnowledgeOverruleSubheading receipt={knowledge} commentId={commentId} />
    );
  }
  const receipt = pastRejectionReceipt(comment);
  if (!receipt) return null;
  return (
    <Text
      as="p"
      size="2"
      weight="medium"
      className="mt-1 flex items-center gap-1.5 text-puntt-neutral-gray-11"
      data-testid={`ask-v2-review-comment-previously-rejected-${commentId}`}
    >
      <XCircle size={16} weight="bold" aria-hidden="true" />
      {previouslyRejectedReceiptLabel(t, receipt, currentUserId)}
    </Text>
  );
}

/**
 * A finding a reviewer already confirmed as an issue: green, under the title,
 * so it reads as agreed work rather than something new.
 */
export function PreviouslyConfirmedSubheading({
  comment,
  commentId,
  currentUserId,
}: {
  comment: CommentWithOptionalTags;
  commentId: string;
  currentUserId?: string;
}) {
  const { t } = useTranslation("ui");
  const receipt = pastApprovalReceipt(comment);
  if (!receipt || pastRejectionReceipt(comment)) return null;
  return (
    <Text
      as="p"
      size="2"
      weight="medium"
      color="green"
      // why: Radix's own green, as the Confirm button uses -- the tailwind
      // `text-puntt-green-11` class rendered gray in the live panel.
      className="mt-1 flex items-center gap-1.5"
      data-testid={`ask-v2-review-comment-previously-confirmed-${commentId}`}
    >
      <CheckCircle size={16} weight="bold" aria-hidden="true" />
      {receiptLabel(
        t,
        "confirmed",
        judgedBy(receipt.decidedBy, receipt.role, currentUserId),
      )}
    </Text>
  );
}
